import { Backdrop, Box, Typography, keyframes } from '@mui/material';
const logo = '/loader.webp';

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const spinRev = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(-360deg); }
`;

const pulseRing = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.1;
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
`;

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const dotBounce = keyframes`
  0%, 80%, 100% {
    transform: scale(0.6);
    opacity: 0.3;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
`;

const glowPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(36, 58, 110, 0.2);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(36, 58, 110, 0.05);
  }
`;

const textGlow = keyframes`
  0%, 100% {
    text-shadow: 0 0 10px rgba(36, 58, 110, 0.3);
  }
  50% {
    text-shadow: 0 0 20px rgba(36, 58, 110, 0.6);
  }
`;

const AppLoader = () => (
  <Backdrop
    open
    sx={{
      zIndex: (t) => t.zIndex.modal + 1,
      background: 'radial-gradient(ellipse at center, var(--appMain50) 0%, var(--appMain100) 100%)',
      backdropFilter: 'blur(8px)'
    }}
  >
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      gap={5}
      sx={{
        animation: `${fadeUp} 0.6s ease-out`
      }}
    >
      {/* ── Main Spinner Container ── */}
      <Box position="relative" width={140} height={140} display="flex" alignItems="center" justifyContent="center">
        {/* Outer Glow Ring */}
        <Box
          sx={{
            position: 'absolute',
            width: 140,
            height: 140,
            borderRadius: '50%',
            background: 'radial-gradient(circle, transparent 60%, var(--appMainMain05) 100%)',
            animation: `${glowPulse} 2s ease-in-out infinite`
          }}
        />

        {/* Pulse Rings */}
        {[
          { inset: -18, delay: 0, borderWidth: 1.5 },
          { inset: -10, delay: 0.4, borderWidth: 2 },
          { inset: -2, delay: 0.8, borderWidth: 1 }
        ].map(({ inset, delay, borderWidth }) => (
          <Box
            key={delay}
            sx={{
              position: 'absolute',
              inset,
              borderRadius: '50%',
              border: `${borderWidth}px solid var(--appMainMain15)`,
              animation: `${pulseRing} 2.5s ease-in-out ${delay}s infinite`
            }}
          />
        ))}

        {/* Outer Arc — Clockwise (Gradient Arc) */}
        <Box
          component="svg"
          viewBox="0 0 140 140"
          width={140}
          height={140}
          sx={{
            position: 'absolute',
            animation: `${spin} 1.2s linear infinite`
          }}
        >
          <circle cx={70} cy={70} r={62} stroke="var(--appMainMain08)" strokeWidth={5} fill="none" />
          <path d="M70 8 A62 62 0 0 1 132 70" stroke="url(#outerArc)" strokeWidth={5} strokeLinecap="round" fill="none" />
          <defs>
            <linearGradient id="outerArc" x1="70" y1="8" x2="132" y2="70" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="var(--appMainMain)" stopOpacity={0.1} />
              <stop offset="50%" stopColor="var(--appMain500)" stopOpacity={0.6} />
              <stop offset="100%" stopColor="var(--appMainMain)" stopOpacity={1} />
            </linearGradient>
          </defs>
        </Box>

        {/* Inner Arc — Counter-Clockwise */}
        <Box
          component="svg"
          viewBox="0 0 100 100"
          width={100}
          height={100}
          sx={{
            position: 'absolute',
            animation: `${spinRev} 1.8s linear infinite`
          }}
        >
          <path d="M50 10 A40 40 0 0 0 10 50" stroke="url(#innerArc)" strokeWidth={4} strokeLinecap="round" fill="none" />
          <defs>
            <linearGradient id="innerArc" x1="50" y1="10" x2="10" y2="50" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="var(--appMain400)" stopOpacity={0} />
              <stop offset="100%" stopColor="var(--appMain400)" stopOpacity={0.8} />
            </linearGradient>
          </defs>
        </Box>

        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, var(--appSurface) 0%, var(--appGrey50) 100%)',
            border: '2px solid var(--appMainWhite)',
            boxShadow: '0 8px 25px var(--appShadowDark)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 2,
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'scale(1.05)',
              boxShadow: '0 12px 30px var(--appShadowDark)'
            }
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="Zenigma Labs"
            sx={{
              width: 42,
              height: 42,
              objectFit: 'contain',
              borderRadius: '50%',
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)'
              }
            }}
          />
        </Box>
      </Box>

      {/* ── Loading Text with Enhanced Effects ── */}
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={2}
        sx={{
          animation: `${fadeUp} 0.6s ease both 0.15s`
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: '1rem',
            letterSpacing: '3px',
            textTransform: 'uppercase',
            background:
              'linear-gradient(135deg, var(--appMainMain) 0%, var(--appMain500) 40%, var(--appMain700) 60%, var(--appMainMain) 100%)',
            backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            animation: `${shimmer} 2.5s linear infinite`
          }}
        >
          Please wait
        </Typography>

        {/* Animated Progress Bar */}
        <Box
          sx={{
            width: 180,
            height: 2,
            borderRadius: 2,
            background: 'var(--appGrey200)',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              width: '40%',
              height: '100%',
              borderRadius: 2,
              background: 'linear-gradient(90deg, var(--appMainMain), var(--appMain400), var(--appMainMain))',
              animation: `${shimmer} 1.8s ease-in-out infinite`
            }}
          />
        </Box>

        {/* Staggered Loading Dots */}
        <Box display="flex" gap="10px" alignItems="center">
          {[
            { color: 'var(--appMainMain)', delay: 0, size: 8 },
            { color: 'var(--appMain500)', delay: 0.2, size: 8 },
            { color: 'var(--appMain300)', delay: 0.4, size: 8 }
          ].map(({ color, delay, size }) => (
            <Box
              key={delay}
              sx={{
                width: size,
                height: size,
                borderRadius: '50%',
                background: color,
                animation: `${dotBounce} 1.4s ease-in-out ${delay}s infinite`,
                boxShadow: `0 0 6px ${color}`
              }}
            />
          ))}
        </Box>

        {/* Subtle Hint Text */}
        <Typography
          sx={{
            fontSize: '0.7rem',
            color: 'var(--appTextSecondary)',
            letterSpacing: '0.5px',
            opacity: 0.7,
            mt: 1,
            animation: `${textGlow} 2s ease-in-out infinite`
          }}
        >
          Loading your data...
        </Typography>
      </Box>
    </Box>
  </Backdrop>
);

export default AppLoader;
